import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Tag,
  Button,
  Modal,
  Progress,
  Radio,
  Checkbox,
  Alert,
  Divider,
} from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FlagOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  TrophyOutlined,
  BookOutlined,
  ReloadOutlined,
  CustomerServiceOutlined,
  ApartmentOutlined,
  ShopOutlined,
  MedicineBoxOutlined,
  CheckOutlined,
  UserOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  LockOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import {
  QUIZ_CONFIG,
  QUIZ_TOPICS,
  getRankInfo,
  formatQuizTime,
} from '../data/pharmacyQuizData.js';
import { pharmacyToolApi } from '../../../api/pharmacy-tool.api.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import '../styles/pharmacy-quiz.css';
import '../styles/pharmacy-tool.css';

const LOCAL_STORAGE_QUIZ_HISTORY_KEY = 'thethao_quiz_history';

export function PharmacyQuizAssessmentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Auth requirement modal state
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [pendingTopicKey, setPendingTopicKey] = useState(null);

  // Navigation mode: 'SELECTION' | 'EXAM' | 'RESULT' | 'REVIEW'
  const [viewMode, setViewMode] = useState('SELECTION');

  // Topic state
  const [selectedTopicKey, setSelectedTopicKey] = useState('sale');

  // Current Exam State
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({}); // { [questionId]: ['A'] or ['A', 'B'] }
  const [flaggedQuestions, setFlaggedQuestions] = useState({}); // { [questionId]: boolean }
  const [timeLeft, setTimeLeft] = useState(QUIZ_CONFIG.timeLimitSeconds);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const timerRef = useRef(null);
  // Mã lượt làm bài do server cấp khi bắt đầu; nộp bài phải kèm mã này (HT-06 S-12)
  const [attemptToken, setAttemptToken] = useState(null);
  const [isStartingExam, setIsStartingExam] = useState(false);
  const [isSubmittingExam, setIsSubmittingExam] = useState(false);
  const isSubmittingRef = useRef(false);

  // Result & Scoring State
  const [scoreResult, setScoreResult] = useState(null);

  // Review Filter
  const [reviewFilter, setReviewFilter] = useState('ALL'); // 'ALL' | 'CORRECT' | 'WRONG'

  // History State
  const [dbHistory, setDbHistory] = useState([]);
  const [historyList, setHistoryList] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_QUIZ_HISTORY_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  // Load database history for logged in user
  const [isLoadingDbHistory, setIsLoadingDbHistory] = useState(false);
  // Phân biệt "chưa tải xong/lỗi" với "đã tải xong nhưng đúng là rỗng (không thi trong 7 ngày)"
  const [dbHistoryLoaded, setDbHistoryLoaded] = useState(false);

  const loadDbHistory = useCallback(async () => {
    if (user && user.id) {
      setIsLoadingDbHistory(true);
      try {
        const res = await pharmacyToolApi.getMyQuizResults();
        const dataList = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : [];
        setDbHistory(dataList);
        setDbHistoryLoaded(true);
      } catch (e) {
        console.warn('Lỗi lấy lịch sử thi từ database:', e);
        setDbHistoryLoaded(false);
      } finally {
        setIsLoadingDbHistory(false);
      }
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      loadDbHistory();
    }
  }, [user, loadDbHistory]);

  // Đã đăng nhập và tải CSDL thành công -> luôn ưu tiên đúng dữ liệu 7 ngày từ CSDL (kể cả khi rỗng),
  // chỉ rơi về lịch sử tạm trên trình duyệt khi chưa đăng nhập hoặc chưa tải/tải lỗi từ CSDL.
  const displayHistory = useMemo(() => {
    if (user && user.id && dbHistoryLoaded) {
      return dbHistory;
    }
    return historyList;
  }, [user, dbHistory, dbHistoryLoaded, historyList]);

  // Direct navigation to full detailed score & review page from history
  const handleOpenHistoryDetail = (item) => {
    let detailData = null;

    if (
      item.scoreResult &&
      item.questions &&
      item.questions.length > 0 &&
      item.userAnswers &&
      item.scoreResult.difficultyStats &&
      (item.scoreResult.difficultyStats['Dễ']?.correct > 0 ||
        item.scoreResult.difficultyStats['TB']?.correct > 0 ||
        item.scoreResult.difficultyStats['Khó']?.correct > 0 ||
        item.score === 0) &&
      item.scoreResult.subTopicStats &&
      Object.keys(item.scoreResult.subTopicStats).length > 0
    ) {
      detailData = {
        item,
        scoreResult: item.scoreResult,
        questions: item.questions,
        userAnswers: item.userAnswers,
        timeSpent: item.timeSpentSeconds || item.timeSpentSec || 0,
        topicKey: item.topicKey,
      };
    } else {
      const numScore = typeof item.score === 'number' ? item.score : parseFloat(item.score) || 0;
      const rankTier =
        QUIZ_CONFIG.rankingTiers.find((t) => numScore >= t.minScore && numScore <= t.maxScore) ||
        QUIZ_CONFIG.rankingTiers[QUIZ_CONFIG.rankingTiers.length - 1];

      let rawAnswers = item.answersData;
      if (typeof rawAnswers === 'string') {
        try {
          rawAnswers = JSON.parse(rawAnswers);
        } catch {
          rawAnswers = [];
        }
      }
      const answersDataList = Array.isArray(rawAnswers) ? rawAnswers : [];

      let restoredQuestions = [];
      let restoredUserAnswers = {};
      let restoredResults = [];

      // Server gắn sẵn câu hỏi, đáp án, lời giải của đúng những câu trong lượt thi này (item.questions).
      // Lịch sử lưu trên trình duyệt từ trước 29/09/2026 có thể còn câu hỏi trong item.questions.
      const questionById = new Map((item.questions || []).map((q) => [q.id, q]));

      if (answersDataList.length > 0) {
        restoredQuestions = answersDataList.map((ans, idx) => {
          const found = questionById.get(ans.questionId);
          return found
            ? { ...found, displayIndex: idx + 1 }
            : {
                id: ans.questionId,
                displayIndex: idx + 1,
                question: `Câu hỏi ${ans.questionId} (không còn trong ngân hàng câu hỏi)`,
                options: [],
                correctAnswers: ans.correctAnswers || [],
                explanation: '',
                difficulty: 'TB',
                subTopic: 'Chuyên môn',
              };
        });

        answersDataList.forEach((ans) => {
          restoredUserAnswers[ans.questionId] = ans.userSelected || [];
        });

        restoredResults = answersDataList.map((ans, idx) => {
          const q = restoredQuestions[idx];
          return {
            questionId: ans.questionId,
            displayIndex: idx + 1,
            question: q,
            userSelected: ans.userSelected || [],
            correctAnswers: ans.correctAnswers || q.correctAnswers || [],
            score: ans.score || 0,
            maxScore: QUIZ_CONFIG.maxPointsPerQuestion,
            status: ans.status || 'incorrect',
            isFullyCorrect: ans.status === 'correct',
            isPartial: ans.status === 'partial',
          };
        });
      }
      // Bản ghi rất cũ không lưu câu đã làm: không biết người dùng làm câu nào, nên chỉ hiện điểm tổng
      // (trước đây dựng bài giả bằng cách bốc ngẫu nhiên 15 câu và rải điểm — đã bỏ vì sai sự thật).

      // ALWAYS compute difficultyStats directly from restoredResults
      const computedDiff = {
        Dễ: { total: 0, correct: 0 },
        TB: { total: 0, correct: 0 },
        Khó: { total: 0, correct: 0 },
      };
      // ALWAYS compute subTopicStats directly from restoredResults
      const computedSub = {};

      restoredResults.forEach((r) => {
        const diff = r.question?.difficulty || 'TB';
        if (!computedDiff[diff]) computedDiff[diff] = { total: 0, correct: 0 };
        computedDiff[diff].total++;
        if (r.isFullyCorrect) computedDiff[diff].correct++;

        const sub = r.question?.subTopic || 'Kỹ năng nền tảng';
        if (!computedSub[sub]) computedSub[sub] = { total: 0, correct: 0, score: 0 };
        computedSub[sub].total++;
        computedSub[sub].score += r.score || 0;
        if (r.isFullyCorrect) computedSub[sub].correct++;
      });

      // Không có chi tiết từng câu: dùng thống kê đã lưu cùng bản ghi (nếu có)
      const parseStats = (raw) => {
        if (typeof raw !== 'string') return raw;
        try {
          return JSON.parse(raw);
        } catch {
          return null;
        }
      };
      const savedDiff = parseStats(item.difficultyStats);
      const finalDiff = restoredResults.length === 0 && savedDiff ? savedDiff : computedDiff;

      // If item has saved subTopicStats with valid keys, use or merge
      const parsedSub = parseStats(item.subTopicStats);
      const finalSub =
        Object.keys(computedSub).length > 0
          ? computedSub
          : parsedSub && Object.keys(parsedSub).length > 0
            ? parsedSub
            : computedSub;

      const hasDetail = restoredResults.length > 0;
      const scoreResultObj = {
        score: numScore,
        rankInfo: rankTier,
        totalQuestions: item.totalQuestions || restoredQuestions.length || 15,
        totalCorrect: hasDetail ? restoredResults.filter((r) => r.isFullyCorrect).length : item.totalCorrect || 0,
        totalPartial: hasDetail ? restoredResults.filter((r) => r.isPartial).length : item.totalPartial || 0,
        totalIncorrect: hasDetail
          ? restoredResults.filter((r) => !r.isFullyCorrect && !r.isPartial).length
          : item.totalIncorrect || 0,
        difficultyStats: finalDiff,
        detailUnavailable: !hasDetail,
        subTopicStats: finalSub,
        results: restoredResults,
      };

      detailData = {
        item,
        scoreResult: scoreResultObj,
        questions: restoredQuestions,
        userAnswers: restoredUserAnswers,
        timeSpent: item.timeSpentSec || item.timeSpentSeconds || 0,
        topicKey: item.topicKey || 'sale',
      };
    }

    // Direct transition into full detailed score page
    setScoreResult(detailData.scoreResult);
    setQuestions(detailData.questions);
    setUserAnswers(detailData.userAnswers);
    if (detailData.topicKey) {
      setSelectedTopicKey(detailData.topicKey);
    }
    setViewMode('RESULT');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sync topic from URL query param if present
  useEffect(() => {
    const topicParam = searchParams.get('topic');
    if (topicParam && ['sale', 'van-hanh', 'marketing', 'duoc', 'comprehensive'].includes(topicParam)) {
      setSelectedTopicKey(topicParam);
    }
  }, [searchParams]);

  // Number of answered questions
  const answeredCount = useMemo(() => {
    return Object.keys(userAnswers).filter(
      (k) => userAnswers[k] && userAnswers[k].length > 0
    ).length;
  }, [userAnswers]);

  // Submit Quiz Ref to avoid stale closures in timer
  const handleSubmitQuizRef = useRef();

  // Timer Effect
  useEffect(() => {
    if (isTimerRunning && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            if (handleSubmitQuizRef.current) {
              handleSubmitQuizRef.current(true);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isTimerRunning, timeLeft]);

  // Topic Metadata Helper
  const currentTopic = useMemo(() => {
    if (selectedTopicKey === 'comprehensive') {
      return {
        key: 'comprehensive',
        title: 'Đề Thi Sát Hạch Tổng Hợp Toàn Diện',
        shortTitle: 'Tổng Hợp 4 Chủ Đề',
        icon: 'TrophyOutlined',
        color: '#d97706',
        tagColor: 'gold',
        description: 'Bài kiểm tra tổng hợp bốc ngẫu nhiên từ cả 4 kho câu hỏi (Sale, Vận hành, Marketing, Chuyên môn Dược) để đánh giá toàn diện năng lực nhân sự.',
        subTopics: ['Tổng hợp 24 nhóm năng lực'],
      };
    }
    return QUIZ_TOPICS.find((t) => t.key === selectedTopicKey) || QUIZ_TOPICS[0];
  }, [selectedTopicKey]);

  // Topic Icon Renderer
  const renderTopicIcon = (iconName, color) => {
    const style = { fontSize: 26, color: color || 'inherit' };
    switch (iconName) {
      case 'CustomerServiceOutlined':
        return <CustomerServiceOutlined style={style} />;
      case 'ApartmentOutlined':
        return <ApartmentOutlined style={style} />;
      case 'ShopOutlined':
        return <ShopOutlined style={style} />;
      case 'MedicineBoxOutlined':
        return <MedicineBoxOutlined style={style} />;
      case 'TrophyOutlined':
        return <TrophyOutlined style={style} />;
      default:
        return <BookOutlined style={style} />;
    }
  };

  // 1. START QUIZ ACTION
  const handleStartExam = async (topicKey = selectedTopicKey) => {
    // Check if user is logged in
    if (!user || !user.id) {
      setPendingTopicKey(topicKey);
      setAuthModalVisible(true);
      return;
    }
    if (isStartingExam) return;

    setSelectedTopicKey(topicKey);
    setIsStartingExam(true);
    try {
      // Server bốc 15 câu (ưu tiên câu chưa làm trong 7 ngày), không gửi kèm đáp án
      const data = await pharmacyToolApi.startQuiz(topicKey);
      setAttemptToken(data.attemptToken);
      setQuestions(data.questions || []);
      setCurrentIndex(0);
      setUserAnswers({});
      setFlaggedQuestions({});
      setTimeLeft(data.timeLimitSeconds || QUIZ_CONFIG.timeLimitSeconds);
      setIsTimerRunning(true);
      setViewMode('EXAM');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.message || 'Không tạo được đề thi. Vui lòng thử lại.');
    } finally {
      setIsStartingExam(false);
    }
  };

  // Confirm Login & redirect
  const handleConfirmLogin = () => {
    setAuthModalVisible(false);
    const returnTopic = pendingTopicKey || selectedTopicKey || 'sale';
    navigate('/login', {
      state: {
        from: {
          pathname: `/tools/trac-nghiem-thuc-chien?topic=${returnTopic}&autostart=true`,
        },
      },
    });
  };

  // Auto start exam if returning from login with autostart flag
  useEffect(() => {
    const shouldAutoStart = searchParams.get('autostart') === 'true';
    if (shouldAutoStart && user && user.id && dbHistoryLoaded && viewMode === 'SELECTION') {
      const topicParam = searchParams.get('topic');
      const validTopic =
        topicParam && ['sale', 'van-hanh', 'marketing', 'duoc', 'comprehensive'].includes(topicParam)
          ? topicParam
          : selectedTopicKey;
      handleStartExam(validTopic);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, searchParams, viewMode, dbHistoryLoaded]);

  // 2. ANSWER SELECTION
  const handleOptionSelect = (questionId, optionKey, isMulti) => {
    setUserAnswers((prev) => {
      const current = prev[questionId] || [];
      if (!isMulti) {
        return { ...prev, [questionId]: [optionKey] };
      } else {
        const already = current.includes(optionKey);
        const updated = already
          ? current.filter((k) => k !== optionKey)
          : [...current, optionKey];
        return { ...prev, [questionId]: updated };
      }
    });
  };

  // Toggle Flag
  const toggleFlag = (questionId) => {
    setFlaggedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  // 3. SUBMIT QUIZ ACTION — server chấm điểm và lưu kết quả (HT-06 S-12)
  const handleSubmitQuiz = async (isAuto = false) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmittingExam(true);
    setIsTimerRunning(false);
    clearInterval(timerRef.current);

    const timeSpent = Math.max(1, QUIZ_CONFIG.timeLimitSeconds - timeLeft);

    try {
      const data = await pharmacyToolApi.submitQuiz({ attemptToken, answers: userAnswers, timeSpentSec: timeSpent });
      const result = { ...data, rankInfo: getRankInfo(data.score) };
      const reviewedQuestions = (data.results || []).map((r) => r.question);
      setScoreResult(result);
      setQuestions(reviewedQuestions);
      setAttemptToken(null);

      // Lưu tạm trên trình duyệt (chỉ gồm các câu vừa làm, kèm đáp án để xem lại)
      const historyItem = {
        id: data.id || Date.now().toString(),
        date: new Date().toISOString(),
        topicKey: currentTopic.key,
        topicTitle: currentTopic.title,
        score: result.score,
        rank: result.rankInfo.rank,
        totalCorrect: result.totalCorrect,
        totalQuestions: result.totalQuestions,
        timeSpentSeconds: timeSpent,
        difficultyStats: result.difficultyStats,
        subTopicStats: result.subTopicStats,
        answersData: result.results.map((r) => ({
          questionId: r.questionId,
          userSelected: r.userSelected,
          correctAnswers: r.correctAnswers,
          score: r.score,
          status: r.status,
        })),
        scoreResult: result,
        questions: reviewedQuestions,
        userAnswers,
      };
      try {
        const updatedHistory = [historyItem, ...historyList].slice(0, 20);
        setHistoryList(updatedHistory);
        localStorage.setItem(LOCAL_STORAGE_QUIZ_HISTORY_KEY, JSON.stringify(updatedHistory));
      } catch (e) { }

      // Server đã lưu kết quả khi chấm; tải lại lịch sử 7 ngày
      loadDbHistory();

      setViewMode('RESULT');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (isAuto) {
        toast.warning('Đã hết 15 phút làm bài! Hệ thống đã tự động nộp và chấm bài của bạn.');
      } else {
        toast.success('Đã nộp bài thành công!');
      }
    } catch (err) {
      if (err.code === 'QUIZ_ATTEMPT_EXPIRED' || err.code === 'QUIZ_ATTEMPT_INVALID') {
        toast.error(err.message);
        setAttemptToken(null);
        setViewMode('SELECTION');
      } else {
        // Lỗi mạng hoặc máy chủ: giữ nguyên bài làm để bấm "Nộp Bài" lại
        toast.error(`${err.message || 'Không nộp được bài'}. Bài làm vẫn được giữ, vui lòng bấm "Nộp Bài" lại.`);
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmittingExam(false);
    }
  };
  handleSubmitQuizRef.current = handleSubmitQuiz;

  // Confirmation modal before manual submit
  const confirmSubmit = () => {
    const unansweredCount = questions.length - answeredCount;

    Modal.confirm({
      title: 'Xác nhận nộp bài trắc nghiệm?',
      icon: <ExclamationCircleOutlined style={{ color: '#d97706' }} />,
      content: (
        <div style={{ marginTop: 8 }}>
          <p>
            Bạn đã hoàn thành <b>{answeredCount}/{questions.length}</b> câu hỏi.
          </p>
          {unansweredCount > 0 ? (
            <Alert
              type="warning"
              showIcon
              message={`Còn ${unansweredCount} câu chưa trả lời. Bạn có chắc chắn muốn nộp bài ngay bây giờ?`}
            />
          ) : (
            <p style={{ color: '#059669' }}>
              Tuyệt vời! Bạn đã chọn đủ đáp án cho tất cả {questions.length} câu hỏi.
            </p>
          )}
        </div>
      ),
      okText: 'Nộp bài ngay',
      cancelText: 'Làm tiếp',
      okButtonProps: { style: { backgroundColor: '#005537', borderColor: '#005537' } },
      onOk: () => handleSubmitQuiz(false),
    });
  };

  // Timer Warning Status
  const timerStatusClass = useMemo(() => {
    if (timeLeft <= 120) return 'danger';
    if (timeLeft <= 300) return 'warning';
    return 'normal';
  }, [timeLeft]);

  // Current Question in Exam Mode
  const currentQuestion = questions[currentIndex] || null;
  const currentSelectedKeys = currentQuestion ? userAnswers[currentQuestion.id] || [] : [];
  const isCurrentMulti = currentQuestion ? currentQuestion.questionType === 'multi' : false;

  return (
    <div className="pharmacy-quiz-wrapper">
      {/* Top Banner Hero */}
      <div className="tool-hero-banner">
        <div className="main-container">
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <Link
              to="/tools"
              style={{
                color: '#d1fae5',
                fontSize: 13,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                textDecoration: 'none',
                background: 'rgba(255, 255, 255, 0.12)',
                padding: '4px 14px',
                borderRadius: 20,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowLeftOutlined /> Trung Tâm Công Cụ
            </Link>

            {user ? (
              <span
                style={{
                  fontSize: 12.5,
                  color: '#a7f3d0',
                  background: 'rgba(255, 255, 255, 0.12)',
                  padding: '4px 14px',
                  borderRadius: 20,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <UserOutlined /> Đã đăng nhập: <b>{user.fullName || user.email}</b>
              </span>
            ) : (
              <span
                style={{
                  fontSize: 12.5,
                  color: '#d1fae5',
                  background: 'rgba(255, 255, 255, 0.08)',
                  padding: '4px 14px',
                  borderRadius: 20,
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                Khách chưa đăng nhập
              </span>
            )}
          </div>

          <Row gutter={[24, 24]} align="middle" justify="space-between">
            <Col xs={24} md={16}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Tag color="#10b981" style={{ fontWeight: 700, borderRadius: 4, padding: '2px 8px' }}>
                  TRẮC NGHIỆM KIẾN THỨC, KỸ NĂNG
                </Tag>
                <span style={{ fontSize: 13, color: '#a7f3d0' }}>
                  Dược sĩ đứng quầy & Quản trị nhà thuốc độc lập
                </span>
              </div>
              <h1 className="tool-hero-title" style={{ fontSize: 30 }}>
                Trắc Nghiệm Kiến Thức & Kỹ Năng Nhân Sự Thực Chiến
              </h1>
              <p className="tool-hero-subtitle" style={{ fontSize: 15 }}>
                Hệ thống câu hỏi tình huống thực tế tại quầy thuốc chia làm 4 chủ đề trọng điểm.
                Không chỉ đánh giá năng lực chính xác theo thang điểm 100, mỗi câu hỏi còn cung cấp bài học
                sâu sắc 3 lớp giúp nhân sự tiến bộ vượt bậc ngay sau khi làm bài.
              </p>
            </Col>

            <Col xs={24} md={8}>
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 14,
                  padding: '18px 20px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                }}
              >
                <div style={{ fontSize: 13, color: '#a7f3d0', fontWeight: 600, marginBottom: 8 }}>
                  QUY CHẾ SÁT HẠCH NĂNG LỰC
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                  <div> <b>15 câu hỏi / lượt test</b></div>
                  <div> <b>15 phút đếm ngược</b> (Tự động nộp khi hết giờ)</div>
                  <div> <b>Thang điểm 100</b></div>
                  <div> <b>Bài học thực chiến 3 lớp</b> (hiện chi tiết sau khi nộp)</div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <div className="main-container" style={{ marginTop: 24 }}>
        {/* =========================================================================
            MÀN HÌNH 1: CHỌN CHỦ ĐỀ & QUY CHẾ THI (SELECTION)
           ========================================================================= */}
        {viewMode === 'SELECTION' && (
          <div>
            {/* Quick Overview Badges */}
            <Row gutter={[16, 16]} style={{ marginBottom: 28 }}>
              <Col xs={12} sm={8}>
                <Card size="small" style={{ borderRadius: 12, textAlign: 'center', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#0284c7' }}>4</div>
                  <div style={{ fontSize: 12.5, color: '#64748b' }}>Chủ đề cốt lõi</div>
                </Card>
              </Col>
              <Col xs={12} sm={8}>
                <Card size="small" style={{ borderRadius: 12, textAlign: 'center', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#7c3aed' }}>24</div>
                  <div style={{ fontSize: 12.5, color: '#64748b' }}>Nhóm năng lực quầy</div>
                </Card>
              </Col>
              <Col xs={12} sm={8}>
                <Card size="small" style={{ borderRadius: 12, textAlign: 'center', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#059669' }}>100%</div>
                  <div style={{ fontSize: 12.5, color: '#64748b' }}>Giải thích 3 lớp chuẩn</div>
                </Card>
              </Col>
            </Row>

            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>
                  Chọn Chủ Đề Khảo Sát & Sát Hạch
                </h2>
                <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0 0' }}>
                  Mỗi bài thi gồm 15 câu tình huống ngẫu nhiên bốc theo tỷ lệ 5 Dễ + 6 Trung bình + 4 Khó.
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tag
                  color="blue"
                  icon={<ClockCircleOutlined />}
                  style={{ padding: '6px 14px', fontSize: 13, borderRadius: 20, fontWeight: 600 }}
                >
                  Quy chuẩn bài thi: 15 phút · 15 câu hỏi tình huống
                </Tag>
              </div>
            </div>

            {/* 4 Topic Cards */}
            <Row gutter={[20, 20]} style={{ marginBottom: 32 }}>
              {QUIZ_TOPICS.map((topic) => (
                <Col xs={24} md={12} key={topic.key}>
                  <div
                    className="topic-card-item"
                    style={{ '--card-color': topic.color }}
                  >
                    <div className="topic-card-header">
                      <div className="topic-icon-box" style={{ background: `${topic.color}15`, color: topic.color }}>
                        {renderTopicIcon(topic.icon, topic.color)}
                      </div>
                      <div style={{ flexGrow: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <Tag color={topic.tagColor} style={{ fontWeight: 600, margin: 0 }}>
                            CHỦ ĐỀ {topic.tagLabel || (topic.key === 'van-hanh' ? 'VẬN HÀNH' : topic.key === 'duoc' ? 'DƯỢC' : topic.key.toUpperCase())}
                          </Tag>
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>60 câu tình huống</span>
                        </div>
                        <h3 className="topic-title">{topic.title}</h3>
                      </div>
                    </div>

                    <p className="topic-desc">{topic.description}</p>

                    <div className="topic-subtopics-list">
                      <div className="topic-subtopics-title">6 Nhóm năng lực trọng điểm:</div>
                      <div>
                        {topic.subTopics.map((sub, sIdx) => (
                          <span key={sIdx} className="subtopic-tag">
                            {sub}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="topic-card-footer">
                      <div style={{ fontSize: 12.5, color: '#64748b' }}>
                        Bốc <b>15 câu</b> (5 Dễ · 6 TB · 4 Khó)
                      </div>
                      <Button
                        type="primary"
                        icon={<ArrowRightOutlined />}
                        onClick={() => handleStartExam(topic.key)}
                        disabled={isStartingExam}
                        style={{
                          backgroundColor: '#005537',
                          borderColor: '#005537',
                          fontWeight: 700,
                          borderRadius: 8,
                          height: 38,
                          padding: '0 18px',
                        }}
                      >
                        Bắt Đầu Làm Bài
                      </Button>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            {/* Comprehensive / Sát hạch toàn diện Banner */}
            <Card
              style={{
                borderRadius: 16,
                background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                color: '#ffffff',
                border: 'none',
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.15)',
                marginBottom: 32,
              }}
            >
              <Row gutter={[24, 24]} align="middle">
                <Col xs={24} md={17}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Tag color="gold" style={{ fontWeight: 700 }}>
                      BÀI THI SÁT HẠCH TOÀN DIỆN
                    </Tag>
                    <span style={{ fontSize: 12.5, color: '#cbd5e1' }}>Đánh giá tổng quát 4 chiều</span>
                  </div>
                  <h3 style={{ fontSize: 20, color: '#ffffff', fontWeight: 800, marginBottom: 8 }}>
                    Kiểm Tra Năng Lực Dược Sĩ Đứng Quầy Toàn Diện (4 Trong 1)
                  </h3>
                  <p style={{ color: '#94a3b8', fontSize: 14, margin: 0, maxWidth: 680, lineHeight: 1.6 }}>
                    Hệ thống sẽ bốc ngẫu nhiên 15 câu trải đều trên cả 4 phân hệ (Bán hàng, Vận hành kho, Marketing và Chuyên môn Dược)
                    để cho bạn bức tranh toàn cảnh về điểm mạnh và điểm nghẽn của bản thân hoặc nhân viên.
                  </p>
                </Col>
                <Col xs={24} md={7} style={{ textAlign: 'right' }}>
                  <Button
                    type="primary"
                    size="large"
                    icon={<TrophyOutlined />}
                    onClick={() => handleStartExam('comprehensive')}
                    disabled={isStartingExam}
                    style={{
                      backgroundColor: '#10b981',
                      borderColor: '#10b981',
                      fontWeight: 700,
                      borderRadius: 10,
                      height: 48,
                      padding: '0 24px',
                      boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
                    }}
                  >
                    Vào Thi Tổng Hợp
                  </Button>
                </Col>
              </Row>
            </Card>

            {/* Test History Section */}
            {displayHistory.length > 0 && (
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700 }}>
                    <ClockCircleOutlined style={{ color: '#005537' }} /> Lịch Sử Sát Hạch 7 Ngày Gần Nhất
                    {user && user.id ? (
                      <Tag color="success" style={{ marginLeft: 8, fontWeight: 600 }}>
                        {dbHistory.length > 0
                          ? `Đồng bộ CSDL tài khoản (${dbHistory.length} bài thi / 7 ngày)`
                          : 'Tài khoản (Đang đồng bộ CSDL...)'}
                      </Tag>
                    ) : (
                      <Tag color="orange" style={{ marginLeft: 8, fontWeight: 500 }}>
                        Lưu tạm trình duyệt (Chưa đăng nhập)
                      </Tag>
                    )}
                  </div>
                }
                extra={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {user && user.id ? (
                      <Button
                        type="link"
                        size="small"
                        icon={<ReloadOutlined />}
                        loading={isLoadingDbHistory}
                        onClick={() => {
                          loadDbHistory();
                          toast.success('Đã tải lại lịch sử từ CSDL!');
                        }}
                      >
                        Làm mới CSDL
                      </Button>
                    ) : (
                      <Link to="/login" style={{ fontSize: 12.5, color: '#005537', fontWeight: 600 }}>
                        Đăng nhập để lưu vào hệ thống
                      </Link>
                    )}
                    <Button
                      type="link"
                      size="small"
                      onClick={() => {
                        localStorage.removeItem(LOCAL_STORAGE_QUIZ_HISTORY_KEY);
                        setHistoryList([]);
                        toast.info('Đã xóa lịch sử làm bài tạm thời trên trình duyệt.');
                      }}
                    >
                      Xóa lịch sử tạm
                    </Button>
                  </div>
                }
                style={{ borderRadius: 14, border: '1px solid #e2e8f0', marginBottom: 28 }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {displayHistory.slice(0, 5).map((item) => {
                    const rankLabel = item.rank || item.ranking || 'ĐẠT';
                    const timeSpent = item.timeSpentSeconds || item.timeSpentSec || 0;
                    return (
                      <div
                        key={item.id}
                        className="history-item-row"
                        onClick={() => handleOpenHistoryDetail(item)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '14px 18px',
                          background: '#f8fafc',
                          borderRadius: 12,
                          border: '1px solid #e2e8f0',
                          flexWrap: 'wrap',
                          gap: 12,
                        }}
                      >
                        <div style={{ flex: 1, minWidth: 220 }}>
                          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                            {item.topicTitle}
                            <Tag color="cyan" style={{ fontSize: 11.5, padding: '0 6px', borderRadius: 4, fontWeight: 500 }}>
                              {timeSpent > 0 ? (timeSpent >= 60 ? `${Math.round(timeSpent / 60)} phút` : `${timeSpent}s`) : '15 phút'}
                            </Tag>
                            {item.userId && (
                              <Tag color="green" style={{ fontSize: 11, padding: '0 6px', borderRadius: 4, fontWeight: 600 }}>
                                Đã lưu CSDL
                              </Tag>
                            )}
                          </div>
                          <div style={{ fontSize: 12.5, color: '#64748b' }}>
                            Ngày làm: {new Date(item.date || item.createdAt).toLocaleDateString('vi-VN')} {new Date(item.date || item.createdAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} · Đúng {item.totalCorrect}/{item.totalQuestions} câu
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 20, fontWeight: 800, color: Number(item.score) >= 70 ? '#059669' : Number(item.score) >= 50 ? '#d97706' : '#dc2626' }}>
                              {typeof item.score === 'number' ? Math.round(item.score * 10) / 10 : item.score} <span style={{ fontSize: 13, fontWeight: 500, color: '#64748b' }}>/ 100</span>
                            </div>
                            <Tag
                              color={Number(item.score) >= 85 ? 'green' : Number(item.score) >= 70 ? 'blue' : Number(item.score) >= 50 ? 'orange' : 'red'}
                              style={{ fontWeight: 600, fontSize: 12 }}
                            >
                              {rankLabel}
                            </Tag>
                          </div>

                          <Button
                            type="primary"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenHistoryDetail(item);
                            }}
                            style={{
                              backgroundColor: '#005537',
                              borderColor: '#005537',
                              borderRadius: 6,
                              fontWeight: 600,
                              padding: '0 12px',
                              height: 32,
                            }}
                          >
                            Xem chi tiết
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* =========================================================================
            MÀN HÌNH 2: PHÒNG THI TRỰC TUYẾN (EXAM ROOM)
           ========================================================================= */}
        {viewMode === 'EXAM' && currentQuestion && (
          <div>
            {/* Sticky Header Bar */}
            <div className="exam-sticky-header">
              <div className="main-container">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Button
                      type="text"
                      icon={<ArrowLeftOutlined />}
                      onClick={() => {
                        Modal.confirm({
                          title: 'Tạm dừng hoặc thoát bài thi?',
                          content: 'Nếu thoát ra, các câu đã làm trong lượt này sẽ không được lưu điểm.',
                          okText: 'Thoát ra',
                          cancelText: 'Làm tiếp',
                          onOk: () => {
                            setIsTimerRunning(false);
                            setViewMode('SELECTION');
                          },
                        });
                      }}
                    >
                      Thoát
                    </Button>
                    <div>
                      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                        {currentTopic.title}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                        Câu {currentIndex + 1} / {questions.length}
                        <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500, marginLeft: 10 }}>
                          (Đã làm: <b style={{ color: '#005537' }}>{answeredCount}</b>/{questions.length} câu)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div className={`timer-pill ${timerStatusClass}`}>
                      <ClockCircleOutlined /> {formatQuizTime(timeLeft)}
                    </div>

                    <Button
                      type="primary"
                      onClick={confirmSubmit}
                      loading={isSubmittingExam}
                      style={{
                        backgroundColor: '#005537',
                        borderColor: '#005537',
                        fontWeight: 700,
                        borderRadius: 8,
                        height: 38,
                        padding: '0 20px',
                      }}
                    >
                      Nộp Bài
                    </Button>
                  </div>
                </div>

                {/* Progress Bar: Tăng theo số câu đã chọn đáp án */}
                <Progress
                  percent={questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0}
                  showInfo={false}
                  strokeColor="#005537"
                  trailColor="#e2e8f0"
                  style={{ margin: '8px 0 0 0' }}
                />
              </div>
            </div>

            <Row gutter={[24, 24]}>
              {/* Question Main Area */}
              <Col xs={24} lg={17}>
                <div className="question-card">
                  {/* Meta Bar */}
                  <div className="question-meta-bar">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Tag color="geekblue" style={{ fontWeight: 700, borderRadius: 4 }}>
                        MÃ: {currentQuestion.id}
                      </Tag>
                      <Tag color="cyan">{currentQuestion.subTopic}</Tag>
                      <Tag
                        color={
                          currentQuestion.difficulty === 'Dễ'
                            ? 'green'
                            : currentQuestion.difficulty === 'TB'
                              ? 'blue'
                              : 'orange'
                        }
                      >
                        Độ khó: {currentQuestion.difficulty}
                      </Tag>
                    </div>

                    <Button
                      type={flaggedQuestions[currentQuestion.id] ? 'primary' : 'default'}
                      danger={flaggedQuestions[currentQuestion.id]}
                      icon={<FlagOutlined />}
                      size="small"
                      onClick={() => toggleFlag(currentQuestion.id)}
                      style={{ borderRadius: 6 }}
                    >
                      {flaggedQuestions[currentQuestion.id] ? 'Đã đánh dấu xem lại' : 'Đánh dấu câu này'}
                    </Button>
                  </div>

                  {/* Question Type Hint */}
                  <div className="question-type-hint">
                    {isCurrentMulti ? (
                      <>
                        <CheckCircleOutlined style={{ color: '#7c3aed' }} />
                        <span style={{ color: '#7c3aed', fontWeight: 700 }}>
                          CÂU HỎI NHIỀU ĐÁP ÁN:
                        </span>{' '}
                        Chọn tất cả các phương án bạn cho là đúng (chọn sai bị trừ điểm theo tỷ lệ).
                      </>
                    ) : (
                      <>
                        <CheckCircleOutlined style={{ color: '#005537' }} />
                        <span style={{ color: '#005537', fontWeight: 700 }}>
                          CÂU HỎI 1 ĐÁP ÁN:
                        </span>{' '}
                        Chỉ chọn duy nhất 1 phương án tối ưu nhất.
                      </>
                    )}
                  </div>

                  {/* Question Content */}
                  <div className="question-title-text">
                    <span style={{ color: '#005537', marginRight: 6 }}>Câu {currentIndex + 1}:</span>
                    {currentQuestion.question}
                  </div>

                  {/* Options List */}
                  <div className="options-list">
                    {currentQuestion.options.map((opt) => {
                      const isSelected = currentSelectedKeys.includes(opt.key);
                      return (
                        <div
                          key={opt.key}
                          className={`option-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleOptionSelect(currentQuestion.id, opt.key, isCurrentMulti)}
                        >
                          <div className="option-key-badge">{opt.key}</div>
                          <div className="option-text">{opt.text}</div>
                          <div>
                            {isCurrentMulti ? (
                              <Checkbox checked={isSelected} style={{ pointerEvents: 'none' }} />
                            ) : (
                              <Radio checked={isSelected} style={{ pointerEvents: 'none' }} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Navigation Buttons */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginTop: 32,
                      paddingTop: 20,
                      borderTop: '1px solid #f1f5f9',
                    }}
                  >
                    <Button
                      disabled={currentIndex === 0}
                      icon={<ArrowLeftOutlined />}
                      onClick={() => {
                        if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
                      }}
                      style={{ height: 40, padding: '0 20px', borderRadius: 8 }}
                    >
                      Câu trước
                    </Button>

                    <div style={{ fontSize: 13, color: '#64748b' }}>
                      Đã làm: <b>{answeredCount}</b> / {questions.length} câu
                    </div>

                    {currentIndex < questions.length - 1 ? (
                      <Button
                        type="primary"
                        onClick={() => setCurrentIndex(currentIndex + 1)}
                        style={{
                          backgroundColor: '#005537',
                          borderColor: '#005537',
                          height: 40,
                          padding: '0 20px',
                          borderRadius: 8,
                          fontWeight: 700,
                        }}
                      >
                        Câu tiếp theo <ArrowRightOutlined />
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        onClick={confirmSubmit}
                        style={{
                          backgroundColor: '#059669',
                          borderColor: '#059669',
                          height: 40,
                          padding: '0 24px',
                          borderRadius: 8,
                          fontWeight: 700,
                        }}
                      >
                        Hoàn thành & Nộp bài <CheckOutlined />
                      </Button>
                    )}
                  </div>
                </div>
              </Col>

              {/* Question Palette Sidebar */}
              <Col xs={24} lg={7}>
                <div className="palette-card">
                  <div className="palette-title">
                    <span>Bảng Điều Hướng ({questions.length} câu)</span>
                    <Tag color="default">
                      {Object.keys(userAnswers).filter((k) => userAnswers[k]?.length > 0).length}/{questions.length} đã làm
                    </Tag>
                  </div>

                  <div className="palette-grid">
                    {questions.map((q, idx) => {
                      const isAnswered = userAnswers[q.id] && userAnswers[q.id].length > 0;
                      const isCurrent = idx === currentIndex;
                      const isFlagged = flaggedQuestions[q.id];

                      let btnClass = 'palette-btn';
                      if (isAnswered) btnClass += ' answered';
                      if (isCurrent) btnClass += ' current';
                      if (isFlagged) btnClass += ' flagged';

                      return (
                        <button
                          key={q.id}
                          className={btnClass}
                          onClick={() => setCurrentIndex(idx)}
                          title={`Câu ${idx + 1} (${q.id})`}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </div>

                  <div className="palette-legend">
                    <div className="palette-legend-item">
                      <div className="palette-dot" style={{ background: '#005537' }} />
                      <span>Đang làm hiện tại (viền đậm)</span>
                    </div>
                    <div className="palette-legend-item">
                      <div className="palette-dot" style={{ background: '#10b981' }} />
                      <span>Đã chọn đáp án</span>
                    </div>
                    <div className="palette-legend-item">
                      <div className="palette-dot" style={{ background: '#e2e8f0', border: '1px solid #cbd5e1' }} />
                      <span>Chưa trả lời</span>
                    </div>
                    <div className="palette-legend-item">
                      <div className="palette-dot" style={{ background: '#fef08a', border: '1px solid #eab308' }} />
                      <span>Đánh dấu xem lại (ô màu vàng)</span>
                    </div>
                  </div>

                  <Divider style={{ margin: '16px 0' }} />

                  <Button
                    block
                    type="primary"
                    onClick={confirmSubmit}
                    loading={isSubmittingExam}
                    style={{
                      backgroundColor: '#005537',
                      borderColor: '#005537',
                      fontWeight: 700,
                      height: 42,
                      borderRadius: 10,
                    }}
                  >
                    Nộp Bài Sát Hạch
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* =========================================================================
            MÀN HÌNH 3: BẢNG KẾT QUẢ & XẾP LOẠI NĂNG LỰC (RESULT)
           ========================================================================= */}
        {viewMode === 'RESULT' && scoreResult && (
          <div>
            {/* User Account / DB Save Status Alert */}
            {user && user.id ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <Tag
                  color="success"
                  icon={<CheckCircleOutlined />}
                  style={{
                    padding: '8px 18px',
                    fontSize: 13.5,
                    borderRadius: 20,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  Đã tự động lưu kết quả bài thi vào tài khoản: <b>{user.fullName || user.email}</b>
                </Tag>
              </div>
            ) : (
              <div style={{ maxWidth: 700, margin: '0 auto 20px auto' }}>
                <Alert
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined style={{ color: '#0284c7' }} />}
                  message="Lưu ý về lưu trữ kết quả bài thi"
                  description={
                    <span>
                      Bạn đang làm bài với tư cách <b>Khách chưa đăng nhập</b>. Hệ thống <b>không lưu kết quả vào cơ sở dữ liệu</b>.{' '}
                      <Link to="/login" style={{ fontWeight: 700, color: '#005537', textDecoration: 'underline' }}>
                        Đăng nhập ngay
                      </Link>{' '}
                      để lưu điểm số và theo dõi lộ trình cải thiện năng lực của bạn!
                    </span>
                  }
                  style={{ borderRadius: 12, border: '1px solid #bae6fd', background: '#f0f9ff' }}
                />
              </div>
            )}

            {/* Hero Result Card */}
            <div
              className="result-hero-card"
              style={{ '--rank-color': scoreResult.rankInfo.color }}
            >
              <div className="score-radial-container">
                <Progress
                  type="circle"
                  percent={scoreResult.score}
                  strokeColor={scoreResult.rankInfo.color}
                  strokeWidth={9}
                  size={150}
                  format={(p) => (
                    <div>
                      <div className="score-number-display">{p}</div>
                      <div className="score-number-sub">/ 100 ĐIỂM</div>
                    </div>
                  )}
                />
              </div>

              <div
                className="rank-badge-box"
                style={{
                  background: scoreResult.rankInfo.bgColor,
                  color: scoreResult.rankInfo.color,
                  border: `1px solid ${scoreResult.rankInfo.borderColor}`,
                }}
              >
                <span>{scoreResult.rankInfo.badge}</span>
                <span>XẾP LOẠI: {scoreResult.rankInfo.rank}</span>
              </div>

              <h2 className="rank-title">{scoreResult.rankInfo.title}</h2>
              <p className="rank-assessment">{scoreResult.rankInfo.assessment}</p>

              {/* Recommendation Callout */}
              <div className="recommendation-box">
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <ThunderboltOutlined style={{ fontSize: 18, color: '#d97706', marginTop: 2 }} />
                  <div>
                    <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>
                      Khuyến nghị hành động từ Dược sĩ chuyên môn:
                    </div>
                    <div style={{ color: '#475569', fontSize: 13.5, marginTop: 4, lineHeight: 1.55 }}>
                      {scoreResult.rankInfo.recommendation}
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Tag color="purple" style={{ fontWeight: 600 }}>
                        Đề xuất đào tạo: {scoreResult.rankInfo.actionCourse}
                      </Tag>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions Bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }} className="no-print">
                <Button
                  type="primary"
                  size="large"
                  icon={<EyeOutlined />}
                  disabled={scoreResult.detailUnavailable}
                  title={scoreResult.detailUnavailable ? 'Bài làm cũ không lưu chi tiết từng câu' : undefined}
                  onClick={() => setViewMode('REVIEW')}
                  style={{
                    backgroundColor: '#005537',
                    borderColor: '#005537',
                    fontWeight: 700,
                    borderRadius: 10,
                    height: 44,
                    padding: '0 24px',
                  }}
                >
                  Xem Lại Từng Câu & Bài Học Thực Chiến
                </Button>

                <Button
                  size="large"
                  icon={<ReloadOutlined />}
                  loading={isStartingExam}
                  onClick={() => handleStartExam(selectedTopicKey)}
                  style={{ borderRadius: 10, height: 44, padding: '0 20px', fontWeight: 600 }}
                >
                  Làm Lại Đề Mới (15 câu khác)
                </Button>

                <Button
                  size="large"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => setViewMode('SELECTION')}
                  style={{ borderRadius: 10, height: 44, padding: '0 20px', fontWeight: 600 }}
                >
                  Chọn Chủ Đề Khác
                </Button>
              </div>
            </div>

            {scoreResult.detailUnavailable && (
              <Alert
                type="info"
                showIcon
                style={{ maxWidth: 760, margin: '0 auto 20px auto', borderRadius: 12 }}
                message="Bài làm này không lưu chi tiết từng câu"
                description="Đây là bài làm từ phiên bản cũ của công cụ, hệ thống chỉ lưu điểm tổng nên không xem lại được đáp án từng câu. Các bài làm mới đều xem lại được đầy đủ đáp án và lời giải."
              />
            )}

            {/* Performance Breakdown Section */}
            <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
              {/* Difficulty breakdown */}
              <Col xs={24} md={10}>
                <Card
                  title={
                    <span style={{ fontWeight: 700, fontSize: 15 }}>
                      Tỷ Lệ Trả Lời Đúng Theo Độ Khó
                    </span>
                  }
                  style={{ borderRadius: 14, height: '100%', border: '1px solid #e2e8f0' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13.5 }}>
                        <span>
                          🟢 Câu Dễ (Kiến thức nền tảng):
                        </span>
                        <b>
                          {scoreResult.difficultyStats?.['Dễ']?.correct ?? 0} / {scoreResult.difficultyStats?.['Dễ']?.total || 5} câu
                        </b>
                      </div>
                      <Progress
                        percent={Math.round(((scoreResult.difficultyStats?.['Dễ']?.correct || 0) / (scoreResult.difficultyStats?.['Dễ']?.total || 5)) * 100)}
                        strokeColor="#059669"
                      />
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13.5 }}>
                        <span>
                          🔵 Câu Trung Bình (Xử lý tình huống quen thuộc):
                        </span>
                        <b>
                          {scoreResult.difficultyStats?.['TB']?.correct ?? 0} / {scoreResult.difficultyStats?.['TB']?.total || 6} câu
                        </b>
                      </div>
                      <Progress
                        percent={Math.round(((scoreResult.difficultyStats?.['TB']?.correct || 0) / (scoreResult.difficultyStats?.['TB']?.total || 6)) * 100)}
                        strokeColor="#0284c7"
                      />
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13.5 }}>
                        <span>
                          🟠 Câu Khó (Bẫy thực tế & ca phức tạp):
                        </span>
                        <b>
                          {scoreResult.difficultyStats?.['Khó']?.correct ?? 0} / {scoreResult.difficultyStats?.['Khó']?.total || 4} câu
                        </b>
                      </div>
                      <Progress
                        percent={Math.round(((scoreResult.difficultyStats?.['Khó']?.correct || 0) / (scoreResult.difficultyStats?.['Khó']?.total || 4)) * 100)}
                        strokeColor="#d97706"
                      />
                    </div>
                  </div>
                </Card>
              </Col>

              {/* Sub-topics breakdown */}
              <Col xs={24} md={14}>
                <Card
                  title={
                    <span style={{ fontWeight: 700, fontSize: 15 }}>
                      Đánh Giá Năng Lực Theo Nhóm Kỹ Năng
                    </span>
                  }
                  style={{ borderRadius: 14, height: '100%', border: '1px solid #e2e8f0' }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {scoreResult.subTopicStats && Object.keys(scoreResult.subTopicStats).length > 0 ? (
                      Object.keys(scoreResult.subTopicStats).map((sub) => {
                        const item = scoreResult.subTopicStats[sub] || {};
                        const total = item.total || 1;
                        const correct = item.correct || 0;
                        const pct = Math.min(100, Math.round((correct / total) * 100));
                        return (
                          <div key={sub}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
                              <span style={{ fontWeight: 600, color: '#334155' }}>{sub}</span>
                              <span style={{ color: '#64748b' }}>
                                Đúng <b>{correct}/{total}</b> câu ({pct}%)
                              </span>
                            </div>
                            <Progress
                              percent={pct}
                              size="small"
                              strokeColor={pct >= 80 ? '#059669' : pct >= 50 ? '#0284c7' : '#dc2626'}
                            />
                          </div>
                        );
                      })
                    ) : (
                      (currentTopic?.subTopics || QUIZ_TOPICS[0]?.subTopics || []).map((sub) => {
                        const estPct = Math.min(100, Math.max(0, Math.round(scoreResult.score || 0)));
                        return (
                          <div key={sub}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
                              <span style={{ fontWeight: 600, color: '#334155' }}>{sub}</span>
                              <span style={{ color: '#64748b' }}>
                                Đạt chuẩn: <b>{estPct}%</b>
                              </span>
                            </div>
                            <Progress
                              percent={estPct}
                              size="small"
                              strokeColor={estPct >= 80 ? '#059669' : estPct >= 50 ? '#0284c7' : '#dc2626'}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>
                </Card>
              </Col>
            </Row>

          </div>
        )}

        {/* =========================================================================
            MÀN HÌNH 4: XEM LẠI CHI TIẾT & BÀI HỌC 3 LỚP (REVIEW)
           ========================================================================= */}
        {viewMode === 'REVIEW' && scoreResult && (
          <div>
            {/* Header of Review */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <Button
                  type="link"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => setViewMode('RESULT')}
                  style={{ padding: 0, fontSize: 14, fontWeight: 600 }}
                >
                  Quay lại bảng điểm kết quả
                </Button>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '6px 0 0 0' }}>
                  Bài Học Thực Chiến 3 Lớp & Rà Soát Chi Tiết
                </h2>
                <p style={{ fontSize: 13.5, color: '#64748b', margin: '4px 0 0 0' }}>
                  Đây là phần giá trị cốt lõi nhất: phân tích tại sao đáp án tối ưu, bẫy tâm lý quen tay và nguyên tắc vàng cho quầy.
                </p>
              </div>

              {/* Filter tabs */}
              <Radio.Group
                value={reviewFilter}
                onChange={(e) => setReviewFilter(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="ALL">
                  Tất cả ({scoreResult.results?.length || 0})
                </Radio.Button>
                <Radio.Button value="WRONG">
                  ❌ Câu sai / cần ôn ({(scoreResult.totalIncorrect || 0) + (scoreResult.totalPartial || 0)})
                </Radio.Button>
                <Radio.Button value="CORRECT">
                  ✅ Câu làm đúng ({scoreResult.totalCorrect || 0})
                </Radio.Button>
              </Radio.Group>
            </div>

            {/* Questions list with 3-layer explanation */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {(scoreResult.results || [])
                .filter((r) => {
                  if (reviewFilter === 'CORRECT') return r.isFullyCorrect;
                  if (reviewFilter === 'WRONG') return !r.isFullyCorrect;
                  return true;
                })
                .map((item, idx) => {
                  const q = item.question || {};
                  const isCorrect = item.isFullyCorrect;
                  const isPartial = item.isPartial;

                  return (
                    <div key={q.id || idx} className="review-question-card">
                      {/* Meta header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 800, fontSize: 16, color: '#005537' }}>
                            Câu {q.displayIndex || idx + 1}
                          </span>
                          <Tag color="blue">{q.id}</Tag>
                          <Tag color="cyan">{q.subTopic}</Tag>
                          <Tag color={q.difficulty === 'Dễ' ? 'green' : q.difficulty === 'TB' ? 'blue' : 'orange'}>
                            {q.difficulty}
                          </Tag>
                          <Tag color="purple">{q.questionTypeLabel}</Tag>
                        </div>

                        <div>
                          {isCorrect ? (
                            <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontWeight: 700, padding: '4px 10px' }}>
                              ĐÚNG (+{item.score} điểm)
                            </Tag>
                          ) : isPartial ? (
                            <Tag color="warning" icon={<ExclamationCircleOutlined />} style={{ fontWeight: 700, padding: '4px 10px' }}>
                              ĐÚNG 1 PHẦN (+{item.score} điểm)
                            </Tag>
                          ) : (
                            <Tag color="error" icon={<CloseCircleOutlined />} style={{ fontWeight: 700, padding: '4px 10px' }}>
                              SAI (0 điểm)
                            </Tag>
                          )}
                        </div>
                      </div>

                      {/* Question Text */}
                      <div style={{ fontSize: 16.5, fontWeight: 600, color: '#0f172a', marginBottom: 18, lineHeight: 1.55 }}>
                        {q.question}
                      </div>

                      {/* Options with correctness indicator */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                        {(q.options || []).map((opt) => {
                          const isOptCorrect = (item.correctAnswers || []).includes(opt.key);
                          const isOptChosen = (item.userSelected || []).includes(opt.key);

                          let optClass = 'review-option-item';
                          if (isOptCorrect && isOptChosen) optClass += ' user-correct';
                          else if (isOptCorrect && !isOptChosen) optClass += ' correct-answer';
                          else if (!isOptCorrect && isOptChosen) optClass += ' user-wrong';

                          return (
                            <div key={opt.key} className={optClass}>
                              <div
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 6,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 700,
                                  fontSize: 13,
                                  background: isOptCorrect ? '#059669' : isOptChosen ? '#dc2626' : '#f1f5f9',
                                  color: isOptCorrect || isOptChosen ? '#ffffff' : '#64748b',
                                  flexShrink: 0,
                                }}
                              >
                                {opt.key}
                              </div>

                              <div style={{ flexGrow: 1, fontSize: 14.5, lineHeight: 1.5 }}>
                                {opt.text}
                              </div>

                              <div style={{ flexShrink: 0 }}>
                                {isOptCorrect && (
                                  <Tag color="success" style={{ fontWeight: 600 }}>
                                    Đáp án tối ưu
                                  </Tag>
                                )}
                                {isOptChosen && !isOptCorrect && (
                                  <Tag color="error" style={{ fontWeight: 600 }}>
                                    Bạn đã chọn (Chưa chuẩn)
                                  </Tag>
                                )}
                                {isOptChosen && isOptCorrect && (
                                  <Tag color="success" style={{ fontWeight: 600 }}>
                                    Bạn đã chọn đúng
                                  </Tag>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* 3-LAYER EXPLANATION CALLOUT */}
                      <div className="explanation-callout">
                        <div className="explanation-callout-header">
                          <MedicineBoxOutlined /> Bài học thực chiến & Phân tích 3 lớp chuyên sâu
                        </div>
                        <div className="explanation-body-text">
                          {q.explanation}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>

      {/* Modal Yêu Cầu Đăng Nhập Để Làm Bài Test */}
      <Modal
        open={authModalVisible}
        onCancel={() => setAuthModalVisible(false)}
        footer={null}
        centered
        width={480}
        styles={{ body: { padding: '32px 28px' } }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#ecfdf5',
              border: '2px solid #a7f3d0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px auto',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15)',
            }}
          >
            <LockOutlined style={{ fontSize: 30, color: '#005537' }} />
          </div>

          <h3 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
            Yêu Cầu Đăng Nhập
          </h3>

          <p style={{ fontSize: 14.5, color: '#475569', lineHeight: 1.6, marginBottom: 20 }}>
            Bạn cần <strong>đăng nhập tài khoản</strong> để bắt đầu làm bài test trắc nghiệm thực chiến.
          </p>

          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: '14px 16px',
              textAlign: 'left',
              marginBottom: 24,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 8 }}>
              Quyền lợi khi đăng nhập làm bài:
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
              <li>Tự động lưu điểm và xếp loại năng lực vào hồ sơ học viên.</li>
              <li>Xem chi tiết giải thích 3 lớp & bài học thực chiến từng câu.</li>
              <li>Theo dõi biểu đồ tiến bộ và lịch sử các lần sát hạch.</li>
            </ul>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Button
              size="large"
              onClick={() => setAuthModalVisible(false)}
              style={{
                borderRadius: 10,
                fontWeight: 600,
                flex: 1,
                height: 44,
              }}
            >
              Để sau
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<LoginOutlined />}
              onClick={handleConfirmLogin}
              style={{
                backgroundColor: '#005537',
                borderColor: '#005537',
                borderRadius: 10,
                fontWeight: 700,
                flex: 1,
                height: 44,
                boxShadow: '0 4px 12px rgba(0, 85, 55, 0.25)',
              }}
            >
              Đăng Nhập Ngay
            </Button>
          </div>

          <div style={{ marginTop: 16, fontSize: 13, color: '#64748b' }}>
            Chưa có tài khoản?{' '}
            <Link
              to="/register"
              onClick={() => setAuthModalVisible(false)}
              style={{ color: '#005537', fontWeight: 700, textDecoration: 'underline' }}
            >
              Đăng ký miễn phí
            </Link>
          </div>
        </div>
      </Modal>
    </div>
  );
}

